/**
 * Auth module — single source of truth for the Shopify session token.
 *
 * Responsibilities split:
 *   - App.tsx (mount + retry loop) calls `refreshToken()`, which is the
 *     ONLY function that initializes App Bridge and asks Shopify for a
 *     fresh JWT. The result is cached in module state.
 *   - apiFetch (lib/api.ts) calls `waitForToken(5000)` / `getToken()` to
 *     read whatever's currently cached. It MUST NOT trigger App Bridge
 *     initialization or read URL params — those concerns live here.
 *
 * Host param persistence:
 *   - Shopify passes ?host=…&shop=…&embedded=1&hmac=…&session=… on every
 *     iframe load.
 *   - React Router's BrowserRouter strips those query params the moment
 *     the user navigates within the SPA (clicks a nav item).
 *   - Without a fallback, we'd fail to re-init App Bridge after any
 *     reload-onto-deep-link or Shopify-side iframe refresh.
 *   - Fix: capture host + shop into sessionStorage on first module load,
 *     fall back to it whenever the URL doesn't have them.
 *
 * Error surface:
 *   - apiFetch never throws a host/apiKey error. If no token is available
 *     after 5 seconds, callers see "Shopify token not ready yet, please
 *     retry".
 */

import createApp, { type ClientApplication } from '@shopify/app-bridge';
import { getSessionToken as bridgeGetSessionToken } from '@shopify/app-bridge-utils';

const SESSION_TOKEN_TIMEOUT_MS = 3000;
const WAIT_POLL_MS = 200;
const STORAGE_HOST_KEY = 'rrwt_shopify_host';
const STORAGE_SHOP_KEY = 'rrwt_shopify_shop';

let cachedApp: ClientApplication | null = null;
let cachedToken: string | null = null;

function timeoutReject(ms: number, label: string): Promise<never> {
  return new Promise((_, reject) => {
    setTimeout(() => reject(new Error(`${label} timed out after ${ms}ms`)), ms);
  });
}

function safeSessionGet(key: string): string | null {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSessionSet(key: string, value: string): void {
  try {
    sessionStorage.setItem(key, value);
  } catch {
    // sessionStorage can throw in private mode or cross-origin iframes;
    // best-effort persistence is acceptable here.
  }
}

/**
 * On module load — capture the Shopify params Shopify Admin passes on
 * every iframe load into sessionStorage. This MUST run before React
 * Router has a chance to strip the query string. Module-level execution
 * happens at app boot, before any component renders.
 */
if (typeof window !== 'undefined') {
  const params = new URLSearchParams(window.location.search);
  const host = params.get('host');
  const shop = params.get('shop');
  if (host) safeSessionSet(STORAGE_HOST_KEY, host);
  if (shop) safeSessionSet(STORAGE_SHOP_KEY, shop);
}

/**
 * Lazy-init App Bridge.
 *   1. Read host from URL.
 *   2. Fall back to sessionStorage (populated on first iframe load).
 *   3. createApp with whichever wins. Both URL and storage are logged
 *      so DevTools shows exactly which path was taken.
 *
 * Returns null if neither URL nor storage has host (genuinely outside
 * any Shopify embed) — never throws.
 */
function ensureApp(): ClientApplication | null {
  if (cachedApp) return cachedApp;

  const fromUrl = new URLSearchParams(window.location.search).get('host');
  const fromStorage = safeSessionGet(STORAGE_HOST_KEY);
  const host = fromUrl ?? fromStorage;
  const apiKey = import.meta.env.VITE_SHOPIFY_API_KEY as string | undefined;

  // Quiet success path — only log a single line saying which source was
  // used. Verbose chain (`host from url: null` / `host from storage: ...` /
  // `using host: ...`) only fires when something is actually wrong, because
  // a literal `null` in the console looks alarming even when the fallback
  // recovered cleanly (the normal case after SPA navigation strips Shopify
  // params from the URL).
  if (host && apiKey) {
    const source = fromUrl ? 'url' : 'sessionStorage';
    cachedApp = createApp({ apiKey, host, forceRedirect: true });
    console.log(`[auth] App Bridge initialized (host source: ${source})`);
    return cachedApp;
  }

  console.warn('[auth] App Bridge init FAILED — diagnostic:');
  console.warn('[auth]   host from url:', fromUrl);
  console.warn('[auth]   host from storage:', fromStorage);
  console.warn('[auth]   using host:', host);
  console.warn('[auth]   apiKey present:', Boolean(apiKey));
  return null;
}

/**
 * App.tsx-only entry point. Asks Shopify for a fresh session token.
 *
 * Order of preference:
 *   1. App Bridge v4 (`window.shopify.idToken()`) — injected by admin.shopify.com
 *      whenever the page has `<meta name="shopify-api-key">`. Always returns a
 *      fresh JWT; Shopify handles caching internally.
 *   2. App Bridge v3 (`@shopify/app-bridge` createApp + getSessionToken) —
 *      backward-compat path. Kept because dev / non-embedded scenarios may
 *      not have window.shopify, and to support older admin shells.
 *
 * Why both: v3's internal token cache stopped returning fresh JWTs inside the
 * modern admin shell, causing every refetch after the first to 401. v4 is the
 * supported, always-fresh path. Falling back keeps non-embedded dev working.
 *
 * 3s hard timeout. Caches on success. Never throws.
 */
export async function refreshToken(): Promise<string | null> {
  // 1. App Bridge v4 — the modern, supported path.
  const shopifyGlobal = (typeof window !== 'undefined'
    ? (window as unknown as { shopify?: { idToken?: () => Promise<string> } }).shopify
    : undefined);
  if (shopifyGlobal && typeof shopifyGlobal.idToken === 'function') {
    try {
      const token = await Promise.race([
        shopifyGlobal.idToken(),
        timeoutReject(SESSION_TOKEN_TIMEOUT_MS, 'window.shopify.idToken'),
      ]);
      if (typeof token === 'string' && token.length > 0) {
        cachedToken = token;
        return token;
      }
    } catch (err) {
      console.warn('[auth] window.shopify.idToken failed, falling back to v3:', err instanceof Error ? err.message : err);
    }
  }

  // 2. App Bridge v3 fallback.
  const app = ensureApp();
  if (!app) {
    return null;
  }
  try {
    const token = await Promise.race([
      bridgeGetSessionToken(app),
      timeoutReject(SESSION_TOKEN_TIMEOUT_MS, 'getSessionToken'),
    ]);
    if (typeof token === 'string' && token.length > 0) {
      cachedToken = token;
      return token;
    }
    return null;
  } catch (err) {
    console.warn('[auth] getSessionToken failed:', err instanceof Error ? err.message : err);
    return null;
  }
}

/**
 * Synchronously return the current cached token (or null). apiFetch uses
 * this. No App Bridge calls. No URL reads.
 */
export function getToken(): string | null {
  return cachedToken;
}

/**
 * Wait up to `timeoutMs` for a cached token to appear. Polls every 200ms.
 * Returns null on timeout. Never throws.
 */
export async function waitForToken(timeoutMs: number): Promise<string | null> {
  if (cachedToken) return cachedToken;
  const startedAt = Date.now();
  while (Date.now() - startedAt < timeoutMs) {
    await new Promise((r) => setTimeout(r, WAIT_POLL_MS));
    if (cachedToken) return cachedToken;
  }
  return cachedToken;
}

/**
 * Compatibility shim. Throws ONLY a generic "token not ready" error —
 * never mentions host or apiKey on the request path.
 */
export async function getAuthToken(): Promise<string> {
  const t = await waitForToken(5000);
  if (t) return t;
  const devToken = import.meta.env.VITE_DEV_ADMIN_TOKEN;
  if (devToken) {
    console.warn('[auth] Using VITE_DEV_ADMIN_TOKEN fallback (dev only)');
    return devToken;
  }
  throw new Error('Shopify token not ready yet, please retry');
}

/**
 * Fire-and-forget: never throws, returns null if no token within 5s.
 */
export async function tryGetAuthToken(): Promise<string | null> {
  return waitForToken(5000);
}

export function isEmbedded(): boolean {
  if (cachedApp) return true;
  const fromUrl = new URLSearchParams(window.location.search).get('host');
  const fromStorage = safeSessionGet(STORAGE_HOST_KEY);
  return Boolean(fromUrl || fromStorage);
}
